import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Bell, CheckCheck, Loader2, Settings } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bookdental/ui";
import type { NotificationType } from "@bookdental/schemas";
import { DEFAULT_NOTIFICATION_PREFS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { playNotificationSound } from "@/lib/notification-sound";
import { useNotificationStream } from "@/lib/notification-stream";
import { useAbrirNotificacao } from "@/lib/abrir-notificacao";
import { iconFor } from "./notification-icons";

/** Fallback polling — real-time vem via SSE (useNotificationStream), estes
 *  intervals só cobrem casos de desconexão/proxy. */
const UNREAD_POLL_MS = 120_000;
const LIST_POLL_MS = 60_000;
const PREVIEW_COUNT = 8;

type NotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string;
  icon: string | null;
  accent: string | null;
  link: string | null;
  readAt: Date | string | null;
  createdAt: Date | string;
  organization?: { id: string; name: string; types: string[]; isPersonal: boolean } | null;
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  // Real-time via SSE — invalida os queries assim que o server empurra evento.
  useNotificationStream();

  const prefsQuery = trpc.notification.getPrefs.useQuery(undefined, {
    staleTime: 60_000,
  });

  // Escopo "todas": o sino é o único lugar que a pessoa olha, e a notificação de pedido novo cai
  // na organização do laboratório mesmo quando ela está na pessoal — ver `escopoDeLeitura`.
  const unreadQuery = trpc.notification.unreadCount.useQuery({ escopo: "todas" }, {
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  // Pequena lista (só os mais recentes) sempre ativa — barato — serve pra
  // detectar novas chegadas e tocar som. Quando popover abre, estende a
  // janela pra PREVIEW_COUNT e ativa polling mais rápido.
  const listQuery = trpc.notification.list.useQuery(
    { take: PREVIEW_COUNT, skip: 0, unreadOnly: false, escopo: "todas" },
    {
      refetchInterval: open ? LIST_POLL_MS : UNREAD_POLL_MS,
      refetchOnWindowFocus: true,
    },
  );

  const markAll = trpc.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const unread = unreadQuery.data?.count ?? 0;
  const hasUnread = unread > 0;
  // useMemo estabiliza a referência — o ?? [] retornaria array novo a cada
  // render, quebrando a dep do useEffect abaixo.
  const items = useMemo<NotificationRow[]>(
    () => (listQuery.data?.items ?? []) as NotificationRow[],
    [listQuery.data],
  );

  // ─── Som quando nova notificação chega ────────────────
  // Compara id+createdAt do mais recente contra o último que já vimos.
  // Não toca no primeiro load (só após ter um "baseline").
  const lastSeenIdRef = useRef<string | null>(null);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    const prefs = prefsQuery.data ?? DEFAULT_NOTIFICATION_PREFS;
    if (items.length === 0) return;
    const newest = items[0];
    const lastSeen = lastSeenIdRef.current;

    if (firstLoadRef.current) {
      lastSeenIdRef.current = newest.id;
      firstLoadRef.current = false;
      return;
    }

    if (lastSeen === newest.id) return;

    // Atualiza ref antes de tocar — evita race com setState
    lastSeenIdRef.current = newest.id;

    if (!prefs.soundEnabled) return;
    const typed = newest.type as NotificationType;
    const perType = prefs.perType[typed];
    if (!perType) return;
    if (!perType.channels.platform) return;
    playNotificationSound(perType.sound);
  }, [items, prefsQuery.data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            hasUnread ? `Notificações — ${unread} não lidas` : "Notificações"
          }
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Bell className={cn("h-[18px] w-[18px]", hasUnread && "animate-wiggle")} />
          {hasUnread && <Badge count={unread} />}
        </button>
      </PopoverTrigger>

      {/* Largura fluida em vez de `w-[340px]` fixo. Com a largura fixa, em 320px o painel era maior
          que a tela — a borda direita caía em -20px e 57 elementos ficavam fora — e, pra tentar
          encaixar, o Radix empurrava o painel pra esquerda, desalinhando-o do sino em toda largura.
          Agora ele acompanha a janela com 12px de folga de cada lado e para de crescer em 380px.

          Sem teto de largura no celular de propósito: com `max-w`, a partir de ~404px o painel
          parava de crescer e voltava a se ancorar no sino, deixando margens desiguais (12 de um
          lado, 38 do outro em 430px). Ocupando a largura disponível, as margens são sempre 12/12.
          A partir de `sm` volta ao painel fixo de 380px ancorado no sino, como no desktop.

          `collisionPadding` garante a folga também quando o Radix reposiciona. `max-h` em `dvh`
          desconta a barra do navegador no celular, e só a LISTA rola: cabeçalho e rodapé ficam. */}
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className="flex max-h-[min(560px,calc(100dvh-5rem))] w-[calc(100vw-24px)] flex-col overflow-hidden border border-zinc-200 bg-white p-0 shadow-xl sm:w-[380px] dark:border-border dark:bg-card"
      >
        <Header
          unread={unread}
          isMarking={markAll.isPending}
          onMarkAll={() => markAll.mutate({ escopo: "todas" })}
        />

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <NotificationList
            items={items}
            loading={listQuery.isLoading && items.length === 0}
            onClose={() => setOpen(false)}
          />
        </div>

        <Footer onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

function Badge({ count }: { count: number }) {
  const display = count > 99 ? "99+" : String(count);
  return (
    <span className="absolute -right-0.5 -top-0.5 flex">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500 opacity-40" />
      <span
        className={cn(
          "relative inline-flex min-w-[16px] items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-600 px-1 text-[9.5px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-card",
          count > 9 ? "h-4 px-1" : "h-4 w-4",
        )}
      >
        {display}
      </span>
    </span>
  );
}

function Header({
  unread,
  isMarking,
  onMarkAll,
}: {
  unread: number;
  isMarking: boolean;
  onMarkAll: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-zinc-100 bg-white px-4 py-2.5 dark:border-border dark:bg-card">
      <div className="min-w-0">
        <h3 className="text-[12.5px] font-bold tracking-tight text-zinc-900 dark:text-foreground">
          Notificações
        </h3>
        <p className="mt-0.5 text-[10.5px] text-zinc-500 dark:text-muted-foreground">
          {unread > 0
            ? `${unread} não ${unread === 1 ? "lida" : "lidas"}`
            : "Tudo em dia"}
        </p>
      </div>
      {unread > 0 && (
        <button
          type="button"
          onClick={onMarkAll}
          disabled={isMarking}
          className="inline-flex h-11 shrink-0 items-center gap-1 rounded-md px-2 text-[10.5px] font-semibold text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-60 sm:h-7 dark:text-primary dark:hover:bg-primary/10"
        >
          {isMarking ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CheckCheck className="h-3 w-3" />
          )}
          Marcar todas
        </button>
      )}
    </div>
  );
}

function NotificationList({
  items,
  loading,
  onClose,
}: {
  items: NotificationRow[];
  loading: boolean;
  onClose: () => void;
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex animate-pulse gap-3 rounded-lg p-2">
            <div className="h-9 w-9 shrink-0 rounded-lg bg-zinc-100 dark:bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-2/3 rounded bg-zinc-100 dark:bg-muted" />
              <div className="h-2 w-full rounded bg-zinc-100 dark:bg-muted" />
              <div className="h-2 w-1/4 rounded bg-zinc-100 dark:bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) return <EmptyState />;

  return (
    <ul className="divide-y divide-zinc-100 dark:divide-border">
      {items.map((n) => (
        <NotificationItem key={n.id} notification={n} onClose={onClose} />
      ))}
    </ul>
  );
}

function NotificationItem({
  notification,
  onClose,
}: {
  notification: NotificationRow;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const { abrir, ehDeOutraOrganizacao } = useAbrirNotificacao();
  const Icon = iconFor(notification.icon);
  const accent = notification.accent || "#2457ff";
  const isUnread = !notification.readAt;
  const deOutraOrganizacao = ehDeOutraOrganizacao(notification);

  const markAsRead = trpc.notification.markAsRead.useMutation({
    onMutate: async () => {
      await utils.notification.unreadCount.cancel();
      const prev = utils.notification.unreadCount.getData();
      if (prev) {
        utils.notification.unreadCount.setData(undefined, {
          count: Math.max(0, prev.count - 1),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev)
        utils.notification.unreadCount.setData(undefined, ctx.prev);
    },
    onSettled: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const handleClick = () => {
    if (isUnread) markAsRead.mutate({ id: notification.id });
    if (abrir(notification)) onClose();
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    locale: ptBR,
    addSuffix: true,
  });

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "group flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left transition-colors",
          isUnread
            ? "bg-brand-50/40 hover:bg-brand-50 dark:bg-primary/5 dark:hover:bg-primary/10"
            : "hover:bg-zinc-50 dark:hover:bg-muted/30",
        )}
      >
        <span
          className={cn(
            "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
            isUnread ? "bg-brand-600 dark:bg-primary" : "bg-transparent",
          )}
        />

        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105"
          style={{
            backgroundColor: `${accent}1a`,
            color: accent,
          }}
        >
          <Icon className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "truncate text-[12.5px]",
              isUnread
                ? "font-bold text-zinc-900 dark:text-foreground"
                : "font-semibold text-zinc-700 dark:text-foreground/80",
            )}
          >
            {notification.title}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-zinc-600 dark:text-muted-foreground">
            {notification.body}
          </p>
          <p className="mt-0.5 text-[10px] font-medium text-zinc-400 dark:text-muted-foreground/80">
            {/* Só quando é de outra organização — na ativa, o nome dela seria ruído em toda linha. */}
            {deOutraOrganizacao && (
              <span className="mr-1.5 rounded bg-zinc-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                {notification.organization!.name}
              </span>
            )}
            {timeAgo}
          </p>
        </div>
      </button>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100/60 text-brand-500 dark:from-primary/10 dark:to-primary/5 dark:text-primary">
        <Bell className="h-5 w-5" />
      </div>
      <p className="mt-0.5 text-[12px] font-semibold text-zinc-700 dark:text-foreground">
        Nenhuma notificação
      </p>
      <p className="max-w-[220px] text-[10.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
        Quando pacientes se cadastrarem pelo link ou houver novidades,
        aparecerão aqui.
      </p>
    </div>
  );
}

function Footer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between gap-2 border-t border-zinc-100 bg-white px-2 py-1.5 dark:border-border dark:bg-card">
      <button
        type="button"
        onClick={() => {
          navigate("/notificacoes");
          onClose();
        }}
        className="h-11 flex-1 rounded-md px-2 text-[11.5px] font-semibold text-brand-700 transition-colors hover:bg-brand-50 sm:h-8 dark:text-primary dark:hover:bg-primary/10"
      >
        Ver todas
      </button>
      <button
        type="button"
        onClick={() => {
          navigate("/conta/preferencias");
          onClose();
        }}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 sm:h-7 sm:w-7 dark:text-muted-foreground dark:hover:bg-muted"
        aria-label="Preferências de notificação"
        title="Preferências de notificação"
      >
        <Settings className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
